// components/daimon/DaimonFace.tsx
'use client';

import React, { useEffect, useRef, useState } from 'react';
import { create } from 'zustand';
import Daily, { type DailyCall, type DailyEventObjectTrack } from '@daily-co/daily-js';
import { Maximize2, Minimize2, Video, X } from '@/components/ui/lucide-memo';
import { AppTooltip } from '@/components/ui/tooltip';
import { DaimonOrb } from './DaimonOrb';
import type { Daimon } from './DaimonVoice';

// THE DAIMON'S FACE (owner, 2026-10-02: a coworker you see, who still acts on the interface). Her
// face is a floating tile, FaceTime's picture in picture, so the app stays usable while she works on
// it. The voice and brain are her own live session; Tavus only lip-syncs it (echo mode): her output
// audio is tapped and sent to the face as 24 kHz PCM, and the user hears the face's copy, so lips and
// sound arrive together (owner, 2026-10-02). Hearing the direct voice instead was tried: the lips
// trailed it. The face's copy once echoed back through the microphone as interruptions; an
// interruption no longer ends the call, and headphones remove the echo. With the camera on she sees
// the user: a frame every few seconds goes to her session as an image.
export const useDaimonFace = create<{ on: boolean; camera: boolean; full: boolean; set: (patch: Partial<{ on: boolean; camera: boolean; full: boolean }>) => void }>((set) => ({
  on: false,
  camera: false,
  // Full screen (owner, 2026-10-02: 'comment on peut se remettre en full screen'): her face alone,
  // large, for a face-to-face moment; the tile again to work in the app beside her.
  full: false,
  set: (patch) => set(patch),
}));

const SAMPLE_RATE = 24000;
const CHUNK = SAMPLE_RATE / 10; // 100 ms of audio per message
const SILENCE_MS = 450;
const GLANCE_MS = 8000;
const TAP = `class Tap extends AudioWorkletProcessor { process(inputs) { const c = inputs[0] && inputs[0][0]; if (c) this.port.postMessage(c.slice(0)); return true; } } registerProcessor('daimon-face-tap', Tap);`;

type Call = { callId: string; roomUrl: string; palId: string };

const toBase64 = (samples: Int16Array) => {
  const bytes = new Uint8Array(samples.buffer, samples.byteOffset, samples.byteLength);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
};

const frameOf = (video: HTMLVideoElement) => {
  if (!video.videoWidth) return null;
  const canvas = document.createElement('canvas');
  const scale = Math.min(1, 640 / video.videoWidth);
  canvas.width = Math.round(video.videoWidth * scale);
  canvas.height = Math.round(video.videoHeight * scale);
  canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.7);
};

export const DaimonFace = ({ daimon }: { daimon: Daimon }) => {
  const { on, camera, full, set } = useDaimonFace();
  const live = daimon.state === 'live';
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const faceRef = useRef<HTMLVideoElement | null>(null);
  const soundRef = useRef<HTMLAudioElement | null>(null);
  const selfRef = useRef<HTMLVideoElement | null>(null);
  const { voiceStream, setVoiceSilent, glance } = daimon;

  // A face only exists inside a live voice call; ending the call puts it away.
  useEffect(() => { if (!live && daimon.state !== 'connecting' && on) set({ on: false, camera: false, full: false }); }, [live, daimon.state, on, set]);
  // Hiding her face also leaves full screen, so the next face opens as the tile.
  useEffect(() => { if (!on && full) set({ full: false }); }, [on, full, set]);
  // Escape leaves full screen.
  useEffect(() => {
    if (!full) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') set({ full: false }); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [full, set]);

  useEffect(() => {
    if (!on || !live || !voiceStream) return;
    let cancelled = false;
    let call: Call | null = null;
    let daily: DailyCall | null = null;
    let audioContext: AudioContext | null = null;
    void (async () => {
      const response = await fetch('/api/daimon/video', { method: 'POST' }).catch(() => null);
      const body = (await response?.json().catch(() => null)) as (Call & { message?: string }) | null;
      if (!response?.ok || !body?.roomUrl) { if (!cancelled) setError(body?.message ?? 'Video could not start.'); return; }
      call = body;
      if (cancelled) { void end(body); return; }
      daily = Daily.createCallObject({ audioSource: false, videoSource: false });
      daily.on('track-started', (event?: DailyEventObjectTrack) => {
        if (!event?.track || event.participant?.local) return;
        const stream = new MediaStream([event.track]);
        if (event.track.kind === 'video' && faceRef.current) { faceRef.current.srcObject = stream; setReady(true); }
        if (event.track.kind === 'audio' && soundRef.current) {
          soundRef.current.srcObject = stream;
          void soundRef.current.play().catch(() => undefined);
          setVoiceSilent(true);
        }
      });
      await daily.join({ url: body.roomUrl, userName: 'Fater', startAudioOff: true, startVideoOff: true });
      if (cancelled) return;

      // Her voice, tapped and sent to the face in 100 ms slices; a pause closes the utterance.
      audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
      await audioContext.audioWorklet.addModule(URL.createObjectURL(new Blob([TAP], { type: 'application/javascript' })));
      const tap = new AudioWorkletNode(audioContext, 'daimon-face-tap');
      audioContext.createMediaStreamSource(voiceStream).connect(tap);
      let pending: number[] = [];
      let inference: string | null = null;
      let quietSince = 0;
      const send = (samples: number[], done: boolean) => {
        if (!daily || !call || !inference) return;
        const pcm = Int16Array.from(samples, (v) => Math.max(-32768, Math.min(32767, Math.round(v * 32767))));
        daily.sendAppMessage({ message_type: 'conversation', event_type: 'conversation.echo', conversation_id: call.callId,
          properties: { modality: 'audio', audio: toBase64(pcm), sample_rate: SAMPLE_RATE, inference_id: inference, done } }, '*');
      };
      tap.port.onmessage = (event: MessageEvent<Float32Array>) => {
        const block = event.data;
        let energy = 0;
        for (const v of block) energy += v * v;
        const speaking = Math.sqrt(energy / block.length) > 0.004;
        const now = performance.now();
        if (speaking) {
          quietSince = 0;
          inference ??= crypto.randomUUID();
        } else if (inference && !quietSince) quietSince = now;
        if (!inference) return;
        for (const v of block) pending.push(v);
        if (quietSince && now - quietSince > SILENCE_MS) {
          send(pending, true);
          pending = [];
          inference = null;
          quietSince = 0;
        } else if (pending.length >= CHUNK) {
          send(pending, false);
          pending = [];
        }
      };
    })().catch(() => { if (!cancelled) setError('Video could not start.'); });
    return () => {
      cancelled = true;
      // Reset as the call goes away, so the next one opens on a clean stage.
      setReady(false);
      setError(null);
      setVoiceSilent(false);
      void audioContext?.close();
      if (daily) void daily.leave().finally(() => void daily?.destroy());
      if (call) void end(call);
    };
  }, [on, live, voiceStream, setVoiceSilent]);

  // Her eyes: the user's camera, a frame every few seconds while it is on.
  useEffect(() => {
    if (!on || !camera) return;
    let stream: MediaStream | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    void navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 360 } }).then((s) => {
      stream = s;
      if (selfRef.current) { selfRef.current.srcObject = s; void selfRef.current.play().catch(() => undefined); }
      timer = setInterval(() => { const frame = selfRef.current ? frameOf(selfRef.current) : null; if (frame) glance(frame); }, GLANCE_MS);
    }).catch(() => set({ camera: false }));
    return () => { if (timer) clearInterval(timer); stream?.getTracks().forEach((t) => t.stop()); };
  }, [on, camera, glance, set]);

  if (!on) return null;
  const control = 'flex h-8 w-8 items-center justify-center rounded-full bg-black/0 text-[#ECECEC] backdrop-blur transition-colors hover:bg-white/[0.12]';
  return (
    <div data-daimon-ui role="region" aria-label="Video call"
      className={full
        ? 'group fixed inset-0 z-[2147483000] flex items-center justify-center bg-black p-6 animate-in fade-in-0 duration-300'
        : 'group fixed bottom-5 right-5 z-[2147483000] w-[360px] overflow-hidden rounded-[20px] border border-[#262626] bg-[#0D0D0D] shadow-[0_16px_48px_rgba(0,0,0,0.55)] animate-in fade-in-0 zoom-in-[0.96] duration-300'}>
      <div className={full ? 'relative aspect-video w-full max-w-[1280px] overflow-hidden rounded-[24px] border border-[#262626] bg-[#0D0D0D]' : 'relative aspect-video w-full'} style={full ? { maxHeight: 'calc(100vh - 48px)' } : undefined}>
        <video ref={faceRef} autoPlay playsInline muted className={`h-full w-full object-cover transition-opacity duration-500 ${ready ? 'opacity-100' : 'opacity-0'}`} />
        {!ready ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <DaimonOrb size={72} level={daimon.level} />
            <p className="px-4 text-center text-[#8E8E8E]" style={{ fontSize: 13 }} aria-live="polite">{error ?? (live ? 'Starting video…' : 'Connecting…')}</p>
          </div>
        ) : null}
        <video ref={selfRef} autoPlay playsInline muted className={`absolute bottom-2 left-2 aspect-video rounded-[10px] border border-[#262626] object-cover ${full ? 'w-[200px]' : 'w-[96px]'} ${camera ? '' : 'hidden'}`} />
        <div className={`absolute right-2 top-2 flex items-center gap-1 rounded-full bg-[#1B1B1B]/80 p-0.5 transition-opacity focus-within:opacity-100 ${full ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
          <AppTooltip content={full ? 'Exit full screen' : 'Full screen'}>
            <button type="button" onClick={() => set({ full: !full })} aria-label={full ? 'Exit full screen' : 'Full screen'} className={control}>
              {full ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
          </AppTooltip>
          <AppTooltip content={camera ? 'Stop showing my camera' : 'Show her my camera'}>
            <button type="button" onClick={() => set({ camera: !camera })} aria-pressed={camera} aria-label={camera ? 'Stop showing my camera' : 'Show her my camera'} className={`${control} ${camera ? '!bg-[#ECECEC] !text-[#0D0D0D]' : ''}`}>
              <Video size={15} />
            </button>
          </AppTooltip>
          <AppTooltip content="Voice only">
            <button type="button" onClick={() => set({ on: false, camera: false, full: false })} aria-label="Voice only" className={control}>
              <X size={15} strokeWidth={2.2} />
            </button>
          </AppTooltip>
        </div>
      </div>
      <audio ref={soundRef} autoPlay />
    </div>
  );
};

async function end(call: Call) {
  await fetch('/api/daimon/video', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ callId: call.callId, palId: call.palId }), keepalive: true }).catch(() => undefined);
}
